import React, { useMemo, useState } from 'react';
import { Alert, Button } from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import StandardLayout from '@/common/layouts/StandardLayout';
import DataTable from '@/common/tableData/DataTable';
import Paginator from '@/pages/common/paginator';
import { fetchInvalidReferralDepthsRest, fixReferralDepthsRest } from '@/services/referrals/referral-depth-service';

const ReferralDepthFixPage: React.FC = () => {
  const [skip, setSkip] = useState(0);
  const [take, setTake] = useState(25);
  const [isFixing, setIsFixing] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['invalidReferralDepths', skip, take],
    queryFn: () => fetchInvalidReferralDepthsRest({ skip, take }),
    staleTime: 30_000,
  });

  const columns = useMemo(() => {
    return [
      { header: 'Child', accessorKey: 'childAlias', enableSorting: false },
      { header: 'Child Email', accessorKey: 'childEmail', enableSorting: false },
      { header: 'Child Depth', accessorKey: 'childDepth', enableSorting: false },
      { header: 'Parent', accessorKey: 'parentAlias', enableSorting: false },
      { header: 'Parent Email', accessorKey: 'parentEmail', enableSorting: false },
      { header: 'Parent Depth', accessorKey: 'parentDepth', enableSorting: false },
      { header: 'Diff', accessorKey: 'diff', enableSorting: false },
    ];
  }, []);

  const headerExtra = (
    <div className="d-flex align-items-center gap-2">
      <Button
        color="primary"
        disabled={isFixing || (data?.items?.length ?? 0) === 0}
        onClick={async () => {
          setIsFixing(true);
          try {
            const result = await fixReferralDepthsRest();
            toast.success(`Depths recomputed (${result.updatedUsersCount} users updated)`);
            await refetch();
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Failed to fix depths');
          } finally {
            setIsFixing(false);
          }
        }}
      >
        Fix
      </Button>
      <Button
        outline
        color="dark"
        disabled={isFixing}
        onClick={async () => {
          try {
            await refetch();
          } catch {
            // ignore
          }
        }}
      >
        Refresh
      </Button>
    </div>
  );

  return (
    <StandardLayout pageTitle="Referral Depth Fix">
      {error ? <Alert color="danger">Unable to load invalid depth records</Alert> : null}

      {isLoading ? <Alert color="info">Loading...</Alert> : null}

      <DataTable
        title="Invalid Depth Records"
        data={data?.items || []}
        columns={columns}
        headerExtra={headerExtra}
        enableSorting={false}
      />

      <Paginator
        take={take}
        skip={skip}
        showing={data?.items?.length || 0}
        totalCount={data?.totalCount || 0}
        setTake={(nextTake) => {
          setTake(nextTake);
          setSkip(0);
        }}
        setSkip={(nextSkip) => setSkip(nextSkip)}
      />

      {data && data.items.length === 0 && !isLoading && (
        <div className="text-muted mt-3">No invalid depth records found.</div>
      )}
    </StandardLayout>
  );
};

export default ReferralDepthFixPage;
